import { CSSProperties } from 'react'
import classes from './ChatWidget.module.scss'
import { ChatBubble, ChatForm, ChatPromoVideo, FeedbackVote, FormOption } from './types'
import StepForm from './StepForm'
import BasketProposalCard from './BasketProposalCard'
import OrderList from './OrderList'
import BalanceCards from './BalanceCards'
import DebtStatusCard from './DebtStatusCard'
import PosLimitsCard from './PosLimitsCard'
import InvoiceList from './InvoiceList'
import ProductCards from './ProductCards'
import DataTable from './DataTable'
import GuaranteeCard from './GuaranteeCard'
import CargoTrackingCard from './CargoTrackingCard'
import SerialNumberCheckCards from './SerialNumberCheckCards'
import BankAccountsCard from './BankAccountsCard'
import CouponCards from './CouponCards'
import CreditCardsCard from './CreditCardsCard'
import AddressCards from './AddressCards'
import CommissionRatesCard from './CommissionRatesCard'
import BasketCommissionRatesCard from './BasketCommissionRatesCard'
import ServiceProductsCard from './ServiceProductsCard'
import BrandCards from './BrandCards'
import CategoryChips from './CategoryChips'
import NavigationActions from './NavigationActions'
import PromoVideoCard from './PromoVideoCard'
import RepresentativesCard from './RepresentativesCard'
import FeedbackBar from './FeedbackBar'
import ExternalLinkCards from './ExternalLinkCards'
import { IconBot, IconPerson } from './icons'
import renderBotText from './chatRichText'

interface Props {
  bubble: ChatBubble
  onFormSelect: (form: ChatForm, opt: FormOption) => void
  onNavigate?: () => void
  /** Tanıtım videosu kapağına tıklanınca panel seviyesindeki oynatıcıyı açar. */
  onPlayVideo?: (video: ChatPromoVideo) => void
  /** Oy yazıcısı. Reddederse FeedbackBar iyimser durumu geri alır (ADR-015). */
  onFeedback?: (bubble: ChatBubble, vote: FeedbackVote, reasonCode?: string, comment?: string) => Promise<void>
  disabled?: boolean
  style?: CSSProperties
}

/** Balon metnin altında yapılandırılmış bir bileşen (form/kart/tablo) taşıyor mu? */
function hasStructuredContent(m: ChatBubble) {
  return Boolean(
    m.form ||
      m.orders?.length ||
      m.balances?.length ||
      m.debtStatus?.companies.length ||
      m.posLimits?.companies.length ||
      m.invoices?.length ||
      m.products?.length ||
      m.brands?.length ||
      m.categories?.length ||
      m.representatives?.length ||
      m.navigationActions?.length ||
      m.externalLinks?.length ||
      m.promoVideo ||
      m.table ||
      m.guaranteeDetail ||
      m.cargoTracking ||
      m.serialNumberChecks?.length ||
      m.bankAccounts?.length ||
      m.coupons?.length ||
      m.creditCards?.length ||
      m.addresses?.length ||
      m.commissionRates ||
      m.basketCommissionRates ||
      m.serviceProducts?.length
  )
}

/** Tek mesaj satırı: avatar + balon (+ varsa adım-form / kartlar / tablo). */
export default function MessageBubble({
  bubble: m,
  onFormSelect,
  onNavigate,
  onPlayVideo,
  onFeedback,
  disabled,
  style,
}: Props) {
  const isUser = m.role === 'user'
  const hasProductSlider = Boolean(m.products && m.products.length > 1)
  const hasProducts = Boolean(m.products && m.products.length > 0)
  const hasNavigationActions = Boolean(m.navigationActions && m.navigationActions.length > 0)
  const bubbleClassName = [
    classes.bubble,
    isUser ? classes.bubbleUser : classes.bubbleBot,
    hasStructuredContent(m) ? classes.bubbleHasForm : '',
    hasProductSlider ? classes.bubbleHasProductSlider : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    // `data-bubble-id`: yanıt gelince bayinin mesajı bu kimlikle bulunup panelin üstüne hizalanır (Faz-44).
    <div className={`${classes.msgRow} ${isUser ? classes.msgRowUser : ''}`} style={style} data-bubble-id={m.id}>
      {isUser ? (
        <span className={classes.userAvatar}>
          <IconPerson />
        </span>
      ) : (
        <span className={classes.botAvatar}>
          <IconBot />
        </span>
      )}
      <div className={bubbleClassName}>
        {isUser ? m.text : renderBotText(m.text)}
        {m.createdAt ? (
          <div className={classes.bubbleTimestamp}>
            {new Date(m.createdAt).toLocaleString('tr-TR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        ) : null}
        {m.basketProposal && <BasketProposalCard proposal={m.basketProposal} />}
        {m.form && (
          <StepForm form={m.form} onSelect={opt => onFormSelect(m.form as ChatForm, opt)} disabled={disabled} />
        )}
        {m.categories && m.categories.length > 0 && <CategoryChips categories={m.categories} />}
        {m.balances && m.balances.length > 0 && <BalanceCards balances={m.balances} />}
        {m.debtStatus && <DebtStatusCard debtStatus={m.debtStatus} />}
        {m.posLimits && <PosLimitsCard posLimits={m.posLimits} />}
        {m.invoices && m.invoices.length > 0 && <InvoiceList invoices={m.invoices} disabled={disabled} />}
        {/* Kullanıcı kararı (Faz-42 inceleme sonrası): ürün listelenirken yönlendirme butonları ("Tümünü Getir",
            "Kutu Hasarlı Ürünlere Git") ürün kartlarının ÜSTÜNDE — arama sonucundaki "Tümünü Getir" ile aynı yer. */}
        {hasProducts && hasNavigationActions && (
          <NavigationActions actions={m.navigationActions!} onNavigate={onNavigate} />
        )}
        {hasProducts && <ProductCards products={m.products!} search={m.productSearch} />}
        {m.brands && m.brands.length > 0 && <BrandCards brands={m.brands} />}
        {/* Faz-42: önce yapılabilecek iş (yönlendirme butonu), sonra "istersen ara" alternatifi
            (temsilci kartı). Sıra GENELDİR — kargo dahil tüm destek yönlendirmeleri bu sırayı izler. */}
        {!hasProducts && hasNavigationActions && (
          <NavigationActions actions={m.navigationActions!} onNavigate={onNavigate} />
        )}
        {m.representatives && m.representatives.length > 0 && (
          <RepresentativesCard representatives={m.representatives} />
        )}
        {m.promoVideo && onPlayVideo && (
          <PromoVideoCard video={m.promoVideo} onPlay={onPlayVideo} disabled={disabled} />
        )}
        {m.table && <DataTable table={m.table} />}
        {m.orders && m.orders.length > 0 && <OrderList orders={m.orders} disabled={disabled} />}
        {m.guaranteeDetail && <GuaranteeCard detail={m.guaranteeDetail} />}
        {m.cargoTracking && <CargoTrackingCard tracking={m.cargoTracking} />}
        {m.serialNumberChecks && m.serialNumberChecks.length > 0 && (
          <SerialNumberCheckCards checks={m.serialNumberChecks} />
        )}
        {m.bankAccounts && m.bankAccounts.length > 0 && <BankAccountsCard accounts={m.bankAccounts} />}
        {m.coupons && m.coupons.length > 0 && <CouponCards coupons={m.coupons} />}
        {m.creditCards && m.creditCards.length > 0 && <CreditCardsCard cards={m.creditCards} />}
        {m.addresses && m.addresses.length > 0 && <AddressCards addresses={m.addresses} />}
        {m.commissionRates && <CommissionRatesCard rates={m.commissionRates} />}
        {m.basketCommissionRates && <BasketCommissionRatesCard rates={m.basketCommissionRates} />}
        {m.serviceProducts && m.serviceProducts.length > 0 && <ServiceProductsCard items={m.serviceProducts} />}
        {m.externalLinks && m.externalLinks.length > 0 && <ExternalLinkCards links={m.externalLinks} />}
        {/* Yalnız SUNUCUDA kalıcılaşmış bot yanıtları oylanabilir; karşılama/yerel balonlarda messageId yoktur. */}
        {!isUser && m.messageId ? (
          <FeedbackBar
            vote={m.feedback?.vote ?? 'none'}
            onVote={(vote, reasonCode, comment) => onFeedback?.(m, vote, reasonCode, comment) ?? Promise.resolve()}
          />
        ) : null}
      </div>
    </div>
  )
}
